import {
  CREDIT_RATES,
  MAX_AI_TOOL_INSTRUCTION_LENGTH,
  type AiTool,
  type MenuSetting,
} from '@lyrmo/shared';
import { useId, useState } from 'react';
import { Icon } from '../../components/Icon';
import { useI18n } from '../../i18n/i18n-context';
import { cx } from '../../lib/cx';
import { matches, menuSections, toolItems, type MenuItem } from './menu-items';
import { MenuFooter, MenuRow, MenuSearch, MenuSection, type MenuAction } from './MenuPanel';
import { moveWithArrows, usePopover, useSearchFocus } from './usePopover';
import './ai-tools.css';

/**
 * A tool to run: a built-in one by name, or one of the writer's own by id, or else what the
 * writer typed, to do to the text just this once, without making a tool of it.
 */
export interface ToolChoice {
  readonly id: string;
  readonly name: string;
  /** What to do to the text, in the writer's words, in place of a tool. */
  readonly instruction?: string | undefined;
}

// The choice that runs what's typed, among the tools' names and ids.
const ONCE = 'once';

/** What a run would format now: the selected text, or else the whole draft, and its words. */
export interface ToolScope {
  readonly selection: boolean;
  readonly words: number;
}

interface AiToolsMenuProps {
  /** The writer's own tools; null until they're known. */
  readonly tools: readonly AiTool[] | null;
  /** Which tools are hidden or pinned: as everyone starts without an account; null until known. */
  readonly settings?: readonly MenuSetting[] | null | undefined;
  /** The tool formatting the draft, while one is. */
  readonly running: ToolChoice | null;
  /** Nothing to format, or the draft can't be changed, such as while dictating. */
  readonly disabled: boolean;
  /** What a run would format now, taken as the menu opens, while the selection is the writer's. */
  readonly measure: () => ToolScope;
  readonly onRun: (tool: ToolChoice) => void;
  /** Stops the tool at work, leaving the draft as it was. */
  readonly onCancel?: (() => void) | undefined;
  /** Opens Manage AI tools, to hide, pin, make, change, or delete them. */
  readonly onManage?: (() => void) | undefined;
  /** Starts a new tool of the writer's own; omitted without an account. */
  readonly onNew?: (() => void) | undefined;
}

/**
 * AI tools, in the editor's toolbar: a menu of tools that format the selected text, or the whole
 * draft, in one go, such as into bullet points: the pinned ones, then the built-in ones, then the
 * writer's own, leaving out those they hid. What's typed in its search finds a tool, or, run
 * just this once, says what to do to the text, such as "a list of pros and cons".
 */
export function AiToolsMenu({
  tools,
  settings,
  running,
  disabled,
  measure,
  onRun,
  onCancel,
  onManage,
  onNew,
}: AiToolsMenuProps) {
  const { t, format } = useI18n();
  const { open, shown, present, leaving, setOpen, rootRef, triggerRef, onBlur } = usePopover(
    !disabled && !running,
  );
  const searchRef = useSearchFocus(shown);
  // What a run would format, taken as the menu opens, while the selection is still the writer's.
  const [scope, setScope] = useState<ToolScope>({ selection: false, words: 0 });
  const [query, setQuery] = useState('');
  const panelId = useId();
  const items = toolItems(tools ?? [], settings, t);
  const sections = menuSections(items);

  /** Closes the menu for what's chosen in it. */
  const leave = (then: () => void) => () => {
    setOpen(false);
    then();
  };
  const run = (item: MenuItem) =>
    leave(() => {
      onRun({ id: item.id, name: item.name });
    });

  const found = (list: readonly MenuItem[]) => list.filter((item) => matches(item, query));
  const pinned = found(sections.pinned);
  const builtIn = found(sections.builtIn);
  const own = found(sections.own);
  const first = pinned[0] ?? builtIn[0] ?? own[0];
  // What's typed, to follow as a tool of the writer's own would be, without making one. Enter
  // runs it when no tool matches; otherwise it waits under them.
  const instruction = query.trim();
  const once: MenuItem | null = instruction
    ? {
        id: ONCE,
        name: instruction,
        hint: t.aiTools.onceHint,
        icon: 'wand',
        builtIn: false,
        hidden: false,
        pinned: false,
      }
    : null;
  const runOnce = leave(() => {
    onRun({ id: ONCE, name: t.aiTools.onceName, instruction });
  });
  // Without any of their own yet, or before they load, the section says so, unless searching.
  const ownNote = query.trim()
    ? null
    : tools === null
      ? t.aiTools.loading
      : items.every((item) => item.builtIn)
        ? t.aiTools.noneYet
        : null;

  const list = (choices: readonly MenuItem[]) => (
    <ul className="menu-list">
      {choices.map((item) => (
        <MenuRow key={item.id} item={item} onSelect={run(item)} />
      ))}
    </ul>
  );

  const actions: MenuAction[] = [];
  if (onNew) {
    actions.push({ label: t.aiTools.newTool, icon: 'plus', onClick: leave(onNew), accent: true });
  }
  if (onManage) actions.push({ label: t.aiTools.manage, icon: 'edit', onClick: leave(onManage) });

  const credits = scope.words * CREDIT_RATES.toolPerWord;
  const words = t.counts.words(scope.words);
  const label = running ? t.aiTools.running(running.name) : t.aiTools.button;
  return (
    <div className="ai-tools" ref={rootRef} onBlur={onBlur}>
      <button
        ref={triggerRef}
        type="button"
        className="toolbar-button"
        aria-label={label}
        title={label}
        aria-expanded={shown}
        aria-controls={panelId}
        aria-busy={running !== null}
        disabled={disabled || running !== null}
        onClick={() => {
          if (!open) {
            setScope(measure());
            setQuery('');
          }
          setOpen((current) => !current);
        }}
      >
        {running ? <span className="ai-tools-spinner" aria-hidden="true" /> : <Icon name="wand" />}
        {/* At work, the spinner alone shows it: which tool is in its label and tooltip. */}
        {!running && <span className="toolbar-button-text">{label}</span>}
        <span className="ai-tools-chevron" aria-hidden="true">
          <Icon name="chevron" />
        </span>
      </button>
      {running && onCancel && (
        <button
          type="button"
          className="ai-tools-cancel"
          aria-label={t.common.cancel}
          title={t.common.cancel}
          onClick={onCancel}
        >
          <Icon name="close" />
        </button>
      )}
      {present && (
        <div
          className={cx('menu-panel ai-tools-panel', leaving && 'is-leaving')}
          id={panelId}
          inert={leaving}
          role="dialog"
          aria-label={t.aiTools.button}
          onKeyDown={moveWithArrows}
        >
          <div className="menu-top">
            <MenuSearch
              inputRef={searchRef}
              value={query}
              onChange={setQuery}
              onEnter={first ? run(first) : once ? runOnce : undefined}
              label={t.aiTools.searchOrSay}
              maxLength={MAX_AI_TOOL_INSTRUCTION_LENGTH}
            />
            <p className="menu-scope">
              <span>
                {scope.selection ? t.aiTools.scopeSelection(words) : t.aiTools.scopeDraft(words)}
              </span>
              <span>
                {t.aiTools.cost(`${format.number(credits)}${t.account.chipUnit(credits)}`)}
              </span>
            </p>
          </div>

          <div className="menu-body">
            {pinned.length > 0 && <MenuSection label={t.menus.pinned}>{list(pinned)}</MenuSection>}
            {builtIn.length > 0 && (
              <MenuSection label={t.aiTools.builtIn}>{list(builtIn)}</MenuSection>
            )}
            {(own.length > 0 || (onManage && ownNote)) && (
              <MenuSection label={t.aiTools.yours}>
                {own.length > 0 && list(own)}
                {ownNote && (
                  <p className="menu-empty" role={tools === null ? 'status' : undefined}>
                    {ownNote}
                  </p>
                )}
              </MenuSection>
            )}
            {query.trim() && !first && (
              <p className="menu-empty" role="status">
                {t.menus.noMatch(query.trim())}
              </p>
            )}
            {once && (
              <MenuSection label={t.aiTools.once}>
                <ul className="menu-list">
                  <MenuRow item={once} onSelect={runOnce} />
                </ul>
              </MenuSection>
            )}
          </div>

          {actions.length > 0 && <MenuFooter hidden={sections.hidden} actions={actions} />}
        </div>
      )}
    </div>
  );
}
